import { XRHandModelFactory } from "three/examples/jsm/webxr/XRHandModelFactory.js";
import { AdditiveBlending, CanvasTexture, FrontSide, MeshMatcapMaterial, MeshStandardMaterial, SkinnedMesh } from "three";
import { mainScene } from "@modules/rendering/mainScene";
import { localPlayerContainer } from "@modules/localplayer/LocalPlayerContainer";
import { capabilitiesService } from "@modules/rendering/CapabilitiesService";
import { animationFrame$ } from "@messages/xr/AnimationFrame";
import { renderManager } from "@modules/xr/RenderManager";
import { ButtonSignal, ButtonType, ControllerButtonMessage } from "flavr-common";
import { gamepadDetectionService } from "./GamepadDetectionService";
import { Service } from "../../module";
import { busService } from "../../bus";
const WEBXR_LOCAL_HAND_PROFILE_PATH = "/webxr/generic-hand/";
const HAND_MODEL_PROFILE = "mesh";
const BUTTON_HOLD_TIME = 1000;
const BUTTON_DOUBLE_CLICK_TIME = 250;
var XrButtonState;
(function (XrButtonState) {
    XrButtonState[XrButtonState["RELEASED"] = 0] = "RELEASED";
    XrButtonState[XrButtonState["PRESSED"] = 1] = "PRESSED";
    XrButtonState[XrButtonState["HOLD"] = 2] = "HOLD";
})(XrButtonState || (XrButtonState = {}));
/** Wrist-to-tip / wrist-to-metacarpal ratio for a fully-open hand. */
const GRAB_OPEN_RATIO = 2.2;
/** Same ratio for a fully-closed fist. */
const GRAB_CLOSED_RATIO = 1.3;
/** Grab wird ab diesem normierten Wert ausgelöst. */
const GRAB_PRESS_THRESHOLD = 0.65;
/** Grab wird erst unter diesem Wert wieder losgelassen. */
const GRAB_RELEASE_THRESHOLD = 0.48;
/** Glättung für noisy Handtracking-Werte (0..1). */
const GRAB_VALUE_SMOOTHING_ALPHA = 0.75;
/** Start soll schnell reagieren, daher wenige Frames. */
const GRAB_START_CONFIRM_FRAMES = 1;
/** Ende soll robuster gegen Tracking-Aussetzer sein, daher mehr Frames. */
const GRAB_END_CONFIRM_FRAMES = 2;
/** Ignoriere kurze Release-Dips direkt nach stabilem Grab. */
const GRAB_RELEASE_GRACE_MS = 50;
/**
 * Frames with no joint reporting a valid pose before we force-hide a hand model. Some runtimes report a
 * hand-tracking → controller switch by leaving the same XRInputSource in place with no valid joint poses,
 * rather than firing an `inputsourceschange` event - three.js's own per-frame visibility update only looks at
 * `inputSource.hand` object truthiness (which stays true), so it never notices and the hand mesh is left
 * rendering at its last tracked pose. A short grace period avoids false hides from single-frame pose gaps.
 */
const STALE_HAND_GRACE_FRAMES = 15;
class HandTrackingControllerService extends Service {
    constructor() {
        super(busService.connection("HandTrackingControllerService"));
        /** Letzter Grab-Zustand pro Controller-UID (stabiler als nur handedness). */
        this.grabState = new Map();
        this.grabLastStableActiveAt = new Map();
        this.grabValueSmoothed = new Map();
        this.grabTransitionCandidate = new Map();
        this.selectState = new Map();
        this.grabButtonState = new Map();
        /** Consecutive frames each hand slot (0/1) has had no joint reporting a valid pose. */
        this.staleHandFrameCount = [0, 0];
        // Holographic material for desktop browsers
        this.holoMaterial = new MeshStandardMaterial({
            color: 0x22ccff,
            emissive: 0x0055cc,
            emissiveIntensity: 0.8,
            roughness: 0.05,
            metalness: 0.6,
            transparent: true,
            opacity: 0.72,
            side: FrontSide,
            depthWrite: false,
            blending: AdditiveBlending
        });
        // Matcap-Material: Shading ohne Lichtquellen – per Canvas generiert, kein externes Asset
        this.questMaterial = new MeshMatcapMaterial({
            matcap: this.createHandMatcapTexture(),
            color: 0xddccbb
        });
        this.xrHandModelFactory = new XRHandModelFactory(null, (object) => {
            this.applyHandMaterial(object);
        }).setPath(WEBXR_LOCAL_HAND_PROFILE_PATH);
        /** Hand models are created once and reused across XR sessions. */
        this.handModels = [null, null];
        this.registeredHandSpaces = [null, null];
        this.onFrameUpdate = (xrFrame) => {
            this.updateSelectButtonStates();
            this.reconcileStaleHandVisibility();
            if (xrFrame === undefined)
                return;
            this.checkGrabGesture(xrFrame);
        };
        this.onInputSourcesChange = (event) => {
            gamepadDetectionService.updateInputSources(event);
        };
        this.onSelectStart = (event) => {
            const uid = this.resolveControllerUid(event);
            if (uid !== undefined) {
                this.onSelectButtonPress(uid);
            }
        };
        this.onSelect = (event) => {
            const uid = this.resolveControllerUid(event);
            if (uid !== undefined) {
                this.tryPromoteSelectToHold(uid, Date.now());
            }
        };
        this.onSelectEnd = (event) => {
            const uid = this.resolveControllerUid(event);
            if (uid !== undefined) {
                this.onSelectButtonRelease(uid);
            }
        };
    }
    onSessionStarted(xrManager, session) {
        this.registerSessionInputListeners(session);
        if (capabilitiesService.isMobileVR) {
            animationFrame$.onInputUpdate$.add(this.onFrameUpdate);
        }
        for (const controllerIndex of [0, 1]) {
            const controllerGrip = xrManager.getControllerGrip(controllerIndex);
            mainScene.add(controllerGrip);
            const handSpace = xrManager.getHand(controllerIndex);
            // Model MUST be created before the "connected" event fires so that
            // XRHandModelFactory's internal "connected" listener (which sets
            // motionController) is registered in time. Creating it lazily inside
            // our own "connected" callback is too late – the event is already gone.
            const handModel = this.ensureHandModel(controllerIndex, handSpace);
            this.ensureHandSpaceListeners(controllerIndex, handSpace);
            handSpace.visible = false;
            if (!handSpace.children.includes(handModel)) {
                handSpace.add(handModel);
            }
            localPlayerContainer.localPlayerRootNode.add(handSpace);
        }
    }
    onSessionEnded(xrManager, session) {
        if (session !== undefined) {
            this.unregisterSessionInputListeners(session);
        }
        animationFrame$.onInputUpdate$.remove(this.onFrameUpdate);
        this.grabState.clear();
        this.grabLastStableActiveAt.clear();
        this.grabValueSmoothed.clear();
        this.grabTransitionCandidate.clear();
        this.selectState.clear();
        this.grabButtonState.clear();
        gamepadDetectionService.clearGamepads();
        for (const controllerIndex of [0, 1]) {
            const controllerGrip = xrManager.getControllerGrip(controllerIndex);
            mainScene.remove(controllerGrip);
            controllerGrip.clear();
            const hand = xrManager.getHand(controllerIndex);
            hand.visible = false;
            hand.removeFromParent();
            const handModel = this.handModels[controllerIndex];
            if (handModel !== null) {
                handModel.removeFromParent();
                this.resetHandModelSkeletonToPose(handModel);
                // Discard the cached model so the next session creates a fresh one via
                // createHandModel(). This re-registers XRHandModelFactory's internal
                // "connected" listener on the new handSpace, which is required for
                // motionController to be set correctly in the new session.
                this.handModels[controllerIndex] = null;
            }
            // Allow ensureHandSpaceListeners to register fresh listeners next session.
            this.registeredHandSpaces[controllerIndex] = null;
        }
    }
    registerSessionInputListeners(session) {
        session.addEventListener("inputsourceschange", this.onInputSourcesChange);
        if (capabilitiesService.isMobileVR) {
            session.addEventListener("selectstart", this.onSelectStart);
            session.addEventListener("select", this.onSelect);
            session.addEventListener("selectend", this.onSelectEnd);
        }
    }
    unregisterSessionInputListeners(session) {
        session.removeEventListener("inputsourceschange", this.onInputSourcesChange);
        session.removeEventListener("selectstart", this.onSelectStart);
        session.removeEventListener("select", this.onSelect);
        session.removeEventListener("selectend", this.onSelectEnd);
    }
    /**
     * Force-hides a hand model whose Group is visible but hasn't had any joint report a valid pose for a while.
     * Needed because three.js only derives hand visibility from `inputSource.hand` truthiness each frame, which
     * doesn't catch the case where the runtime stops giving joint poses without ever removing the input source
     * (see STALE_HAND_GRACE_FRAMES). Without this, a stale hand mesh can be left lurking at its last tracked pose.
     */
    reconcileStaleHandVisibility() {
        for (const controllerIndex of [0, 1]) {
            const handModel = this.handModels[controllerIndex];
            const handSpace = handModel === null || handModel === void 0 ? void 0 : handModel.parent;
            if (handModel === null || handSpace === null || handSpace === undefined || !handSpace.visible) {
                this.staleHandFrameCount[controllerIndex] = 0;
                continue;
            }
            if (this.hasAnyValidJointPose(handSpace)) {
                this.staleHandFrameCount[controllerIndex] = 0;
                continue;
            }
            this.staleHandFrameCount[controllerIndex]++;
            if (this.staleHandFrameCount[controllerIndex] >= STALE_HAND_GRACE_FRAMES) {
                handSpace.visible = false;
                this.resetHandModelSkeletonToPose(handModel);
            }
        }
    }
    hasAnyValidJointPose(handSpace) {
        // Three.js stores joint Groups as a plain object on the hand Group (see showHandWhenJointsReady above).
        const joints = handSpace.joints;
        if (joints === undefined || typeof joints !== "object" || joints === null)
            return true; // unknown shape, don't fight it
        const jointMap = joints;
        return Object.values(jointMap).some(joint => joint.visible);
    }
    updateSelectButtonStates() {
        const time = Date.now();
        for (const [uid, state] of this.selectState) {
            if (state.state !== XrButtonState.PRESSED)
                continue;
            this.tryPromoteToHold(uid, time, this.selectState, ButtonType.SELECT);
        }
        for (const [uid, state] of this.grabButtonState) {
            if (state.state !== XrButtonState.PRESSED)
                continue;
            this.tryPromoteToHold(uid, time, this.grabButtonState, ButtonType.GRAB);
        }
    }
    tryPromoteSelectToHold(uid, time) {
        this.tryPromoteToHold(uid, time, this.selectState, ButtonType.SELECT);
    }
    tryPromoteToHold(uid, time, stateMap, buttonType) {
        const state = stateMap.get(uid);
        if (state === undefined || state.state !== XrButtonState.PRESSED)
            return;
        if (time - state.lastChangeTimeStamp <= BUTTON_HOLD_TIME)
            return;
        this.connection.send(new ControllerButtonMessage(uid, buttonType, ButtonSignal.HOLD, time));
        state.state = XrButtonState.HOLD;
    }
    /**
     * Erkennt Grab über Finger-Curl von Mittel- und Ringfinger.
     * Sendet ControllerButtonMessage (GRAB) mit vollständigem Press/Hold/Release-Ablauf
     * analog zu InputDevice.onButtonPress / onButtonRelease / updateButtonState.
     */
    checkGrabGesture(xrFrame) {
        var _a, _b;
        if (xrFrame.getJointPose === undefined)
            return;
        const getJointPose = xrFrame.getJointPose.bind(xrFrame);
        const referenceSpace = renderManager.xrManager.getReferenceSpace();
        if (referenceSpace === null || referenceSpace === undefined)
            return;
        for (const inputSource of xrFrame.session.inputSources) {
            if (inputSource.hand === null || inputSource.hand === undefined)
                continue;
            const controllerInfo = gamepadDetectionService.getControllerInformationForInputSource(inputSource);
            if (controllerInfo === undefined || controllerInfo.uid === undefined)
                continue;
            const uid = controllerInfo.uid;
            const middleGrabValue = this.getFingerCurlGrabValue(getJointPose, referenceSpace, inputSource.hand, "middle");
            const ringGrabValue = this.getFingerCurlGrabValue(getJointPose, referenceSpace, inputSource.hand, "ring");
            if (middleGrabValue === undefined && ringGrabValue === undefined)
                continue;
            // Robuste Fusion: nutze den stärkeren Fingerwert; falls beide vorhanden sind,
            // berücksichtige zusätzlich den Mittelwert gegen Ausreißer.
            let grabValue;
            if (middleGrabValue !== undefined && ringGrabValue !== undefined) {
                const averagedGrabValue = (middleGrabValue + ringGrabValue) * 0.5;
                grabValue = Math.max(middleGrabValue, ringGrabValue, averagedGrabValue);
            }
            else {
                grabValue = (_a = middleGrabValue !== null && middleGrabValue !== void 0 ? middleGrabValue : ringGrabValue) !== null && _a !== void 0 ? _a : 0;
            }
            const smoothedGrabValue = this.smoothGrabValue(uid, grabValue);
            const wasGrabbing = (_b = this.grabState.get(uid)) !== null && _b !== void 0 ? _b : false;
            const threshold = wasGrabbing ? GRAB_RELEASE_THRESHOLD : GRAB_PRESS_THRESHOLD;
            const isGrabbing = smoothedGrabValue >= threshold;
            const now = Date.now();
            const transitioned = this.tryCommitGrabStateTransition(uid, wasGrabbing, isGrabbing, now);
            // Keep gesture detection fast and responsive.
            this.syncGrabButtonState(controllerInfo, isGrabbing);
            if (transitioned !== undefined) {
                this.grabState.set(uid, transitioned);
                if (transitioned) {
                    this.grabLastStableActiveAt.set(uid, now);
                }
                if (transitioned) {
                    this.onGrabButtonPress(uid);
                }
                else {
                    this.onGrabButtonRelease(uid);
                }
            }
        }
    }
    syncGrabButtonState(controllerInfo, isGrabbing) {
        var _a;
        const grabButton = (_a = controllerInfo.gamepad.buttons) === null || _a === void 0 ? void 0 : _a[1];
        if (grabButton === undefined)
            return;
        grabButton.value = isGrabbing ? 1 : 0;
        grabButton.pressed = isGrabbing;
        grabButton.touched = isGrabbing;
    }
    smoothGrabValue(uid, value) {
        const prev = this.grabValueSmoothed.get(uid);
        const smoothed = prev === undefined ? value : prev + (value - prev) * GRAB_VALUE_SMOOTHING_ALPHA;
        this.grabValueSmoothed.set(uid, smoothed);
        return smoothed;
    }
    tryCommitGrabStateTransition(uid, currentState, detectedState, now) {
        if (detectedState === currentState) {
            this.grabTransitionCandidate.delete(uid);
            if (currentState) {
                this.grabLastStableActiveAt.set(uid, now);
            }
            return undefined;
        }
        if (currentState && !detectedState) {
            const lastStableAt = this.grabLastStableActiveAt.get(uid);
            if (lastStableAt !== undefined && now - lastStableAt < GRAB_RELEASE_GRACE_MS) {
                this.grabTransitionCandidate.delete(uid);
                return undefined;
            }
        }
        const candidate = this.grabTransitionCandidate.get(uid);
        if (candidate === undefined || candidate.targetState !== detectedState) {
            this.grabTransitionCandidate.set(uid, { targetState: detectedState, frames: 1 });
            return undefined;
        }
        candidate.frames += 1;
        const requiredFrames = detectedState ? GRAB_START_CONFIRM_FRAMES : GRAB_END_CONFIRM_FRAMES;
        if (candidate.frames < requiredFrames) {
            return undefined;
        }
        this.grabTransitionCandidate.delete(uid);
        return detectedState;
    }
    getFingerCurlGrabValue(getJointPose, referenceSpace, hand, fingerPrefix) {
        const wrist = hand.get("wrist");
        const fingerTip = hand.get(`${fingerPrefix}-finger-tip`);
        const fingerMeta = hand.get(`${fingerPrefix}-finger-metacarpal`);
        if (wrist === undefined || fingerTip === undefined || fingerMeta === undefined)
            return undefined;
        if (getJointPose === undefined)
            return undefined;
        const wristPose = getJointPose(wrist, referenceSpace);
        const fingerTipPose = getJointPose(fingerTip, referenceSpace);
        const fingerMetaPose = getJointPose(fingerMeta, referenceSpace);
        if (wristPose === null || wristPose === undefined)
            return undefined;
        if (fingerTipPose === null || fingerTipPose === undefined)
            return undefined;
        if (fingerMetaPose === null || fingerMetaPose === undefined)
            return undefined;
        const wristPos = wristPose.transform.position;
        const tipPos = fingerTipPose.transform.position;
        const metaPos = fingerMetaPose.transform.position;
        const wristToTipDistance = Math.sqrt(Math.pow(wristPos.x - tipPos.x, 2) +
            Math.pow(wristPos.y - tipPos.y, 2) +
            Math.pow(wristPos.z - tipPos.z, 2));
        const wristToMetaDistance = Math.sqrt(Math.pow(wristPos.x - metaPos.x, 2) +
            Math.pow(wristPos.y - metaPos.y, 2) +
            Math.pow(wristPos.z - metaPos.z, 2));
        const ratio = wristToTipDistance / Math.max(wristToMetaDistance, 0.001);
        return Math.max(0, Math.min(1, (GRAB_OPEN_RATIO - ratio) / (GRAB_OPEN_RATIO - GRAB_CLOSED_RATIO)));
    }
    resolveControllerUid(event) {
        const xrEvent = event;
        const inputSource = xrEvent.inputSource;
        if (inputSource === undefined)
            return;
        // Physical controllers already get ButtonType.SELECT via the native gamepad-button pipeline
        // (ControllerInputDevice.update() polling BUTTON_MAPPING's trigger index). These WebXR-level
        // select/selectstart/selectend session events exist only to detect hand-tracking pinches, which have no
        // gamepad object to poll - handling them for physical controllers too double-fires every trigger press.
        if (inputSource.hand === undefined || inputSource.hand === null)
            return;
        const controllerInfo = gamepadDetectionService.getControllerInformationForInputSource(inputSource);
        return controllerInfo === null || controllerInfo === void 0 ? void 0 : controllerInfo.uid;
    }
    getOrCreateSelectState(uid) {
        let state = this.selectState.get(uid);
        if (state === undefined) {
            state = { state: XrButtonState.RELEASED, lastChangeTimeStamp: 0, doubleClick: false };
            this.selectState.set(uid, state);
        }
        return state;
    }
    getOrCreateGrabState(uid) {
        let state = this.grabButtonState.get(uid);
        if (state === undefined) {
            state = { state: XrButtonState.RELEASED, lastChangeTimeStamp: 0, doubleClick: false };
            this.grabButtonState.set(uid, state);
        }
        return state;
    }
    sendSelectButtonMessage(uid, input, time) {
        this.connection.send(new ControllerButtonMessage(uid, ButtonType.SELECT, input, time));
    }
    sendGrabButtonMessage(uid, input, time) {
        this.connection.send(new ControllerButtonMessage(uid, ButtonType.GRAB, input, time));
    }
    onSelectButtonPress(uid) {
        const state = this.getOrCreateSelectState(uid);
        if (state.state !== XrButtonState.RELEASED)
            return;
        const time = Date.now();
        this.sendSelectButtonMessage(uid, ButtonSignal.PRESSED, time);
        if (time - state.lastChangeTimeStamp > BUTTON_DOUBLE_CLICK_TIME) {
            state.doubleClick = false;
        }
        state.state = XrButtonState.PRESSED;
        state.lastChangeTimeStamp = time;
    }
    onGrabButtonPress(uid) {
        const state = this.getOrCreateGrabState(uid);
        if (state.state !== XrButtonState.RELEASED)
            return;
        const time = Date.now();
        this.sendGrabButtonMessage(uid, ButtonSignal.PRESSED, time);
        if (time - state.lastChangeTimeStamp > BUTTON_DOUBLE_CLICK_TIME) {
            state.doubleClick = false;
        }
        state.state = XrButtonState.PRESSED;
        state.lastChangeTimeStamp = time;
    }
    onSelectButtonRelease(uid) {
        const state = this.getOrCreateSelectState(uid);
        if (state.state === XrButtonState.RELEASED)
            return;
        const time = Date.now();
        if (state.state === XrButtonState.PRESSED) {
            if (time - state.lastChangeTimeStamp > BUTTON_HOLD_TIME) {
                this.sendSelectButtonMessage(uid, ButtonSignal.HOLD, time);
                state.state = XrButtonState.HOLD;
            }
            else {
                const input = state.doubleClick ? ButtonSignal.DOUBLE_CLICK : ButtonSignal.CLICKED;
                this.sendSelectButtonMessage(uid, input, time);
                state.doubleClick = !state.doubleClick;
            }
        }
        if (state.state === XrButtonState.HOLD) {
            this.sendSelectButtonMessage(uid, ButtonSignal.HOLD_RELEASED, time);
            state.doubleClick = false;
        }
        this.sendSelectButtonMessage(uid, ButtonSignal.RELEASED, time);
        state.state = XrButtonState.RELEASED;
        state.lastChangeTimeStamp = time;
    }
    onGrabButtonRelease(uid) {
        const state = this.getOrCreateGrabState(uid);
        if (state.state === XrButtonState.RELEASED)
            return;
        const time = Date.now();
        if (state.state === XrButtonState.PRESSED) {
            if (time - state.lastChangeTimeStamp > BUTTON_HOLD_TIME) {
                this.sendGrabButtonMessage(uid, ButtonSignal.HOLD, time);
                state.state = XrButtonState.HOLD;
            }
            else {
                const input = state.doubleClick ? ButtonSignal.DOUBLE_CLICK : ButtonSignal.CLICKED;
                this.sendGrabButtonMessage(uid, input, time);
                state.doubleClick = !state.doubleClick;
            }
        }
        if (state.state === XrButtonState.HOLD) {
            this.sendGrabButtonMessage(uid, ButtonSignal.HOLD_RELEASED, time);
            state.doubleClick = false;
        }
        this.sendGrabButtonMessage(uid, ButtonSignal.RELEASED, time);
        state.state = XrButtonState.RELEASED;
        state.lastChangeTimeStamp = time;
    }
    ensureHandModel(controllerIndex, handSpace) {
        let handModel = this.handModels[controllerIndex];
        if (handModel === null) {
            handModel = this.xrHandModelFactory.createHandModel(handSpace, HAND_MODEL_PROFILE);
            this.handModels[controllerIndex] = handModel;
        }
        return handModel;
    }
    ensureHandSpaceListeners(controllerIndex, handSpace) {
        if (this.registeredHandSpaces[controllerIndex] === handSpace)
            return;
        const xrHandSpace = handSpace;
        xrHandSpace.addEventListener("connected", (event) => {
            const hasHandData = event.data.hand !== undefined && event.data.hand !== null;
            if (!hasHandData)
                return;
            const handModel = this.handModels[controllerIndex];
            if (handModel !== null) {
                // Reset bones to bind pose before making visible so the first visible
                // frame never shows stale joint positions from before the disconnect.
                this.resetHandModelSkeletonToPose(handModel);
                if (!handSpace.children.includes(handModel)) {
                    handSpace.add(handModel);
                }
            }
            if (capabilitiesService.isMobileVR) {
                // On Quest browser, joint poses are not all valid on the very first frame
                // after a hand reconnects. XRHandMeshModel.updateMesh() only writes a bone
                // when the corresponding joint Group has visible=true (valid pose from the
                // XR frame). Any joint that is still null on that frame stays at bind-pose
                // while the rest are updated → partial deformation.
                //
                // Fix: poll each frame until ALL joints in the hand space report visible=true,
                // then reveal the model. We give up after MAX_JOINT_WAIT_FRAMES to avoid
                // permanently hiding a hand when a joint is persistently untracked.
                this.showHandWhenJointsReady(handSpace);
            }
            else {
                handSpace.visible = true;
            }
        });
        xrHandSpace.addEventListener("disconnected", () => {
            handSpace.visible = false;
            const handModel = this.handModels[controllerIndex];
            if (handModel === null)
                return;
            // Reset skeleton to bind pose so bones don't stay frozen in the last
            // tracked position while the hand is out of tracking range.
            this.resetHandModelSkeletonToPose(handModel);
            // Null out motionController and clear loaded mesh children so that
            // XRHandModelFactory re-creates the XRHandMeshModel with the correct
            // handedness on the next "connected" event.
            //
            // Background: WebXR may reassign input sources to different controller
            // slots (0 / 1) after a tracking loss – e.g. the right hand can come
            // back in slot 0 (previously left). XRHandModelFactory only sets up
            // motionController once (guard: `!handModel.motionController`), so
            // without this reset the old, mirrored mesh drives the bones → deformed
            // hand model on Quest 3.
            const handModelAny = handModel;
            if (handModelAny.motionController !== null && handModelAny.motionController !== undefined) {
                handModel.clear(); // removes the loaded GLB mesh
                handModelAny.motionController = null;
            }
        });
        this.registeredHandSpaces[controllerIndex] = handSpace;
    }
    /**
     * Polls per-frame until ALL joint Groups in handSpace have visible=true
     * (valid XR joint pose) AND the hand model has at least one SkinnedMesh
     * child (i.e. the GLB mesh has finished loading after a motionController
     * reset), then reveals the hand space.
     *
     * Three.js only updates a skinned bone from a joint when the joint Group
     * has visible=true. On Quest 3 browser the first 1–N frames after a
     * reconnect can have partial joint coverage, leaving un-updated bones at
     * bind-pose → deformed mesh. Waiting until every joint is ready avoids this.
     *
     * @param handSpace  The Group returned by xrManager.getHand(i).
     * @param maxFrames  Safety limit – show anyway after this many frames.
     */
    showHandWhenJointsReady(handSpace, maxFrames = 30) {
        let attempts = 0;
        const check = () => {
            attempts++;
            // Three.js stores joint Groups as a plain object on the hand Group.
            const joints = handSpace.joints;
            if (joints !== undefined && typeof joints === "object" && joints !== null) {
                const jointMap = joints;
                const allJointsReady = Object.values(jointMap).every((j) => j.visible);
                // Also wait for the SkinnedMesh to be present – it is loaded
                // asynchronously and may not exist yet if motionController was reset
                // during disconnect (handedness-change recovery).
                let hasMesh = false;
                handSpace.traverse((child) => {
                    if (child instanceof SkinnedMesh)
                        hasMesh = true;
                });
                if ((!allJointsReady || !hasMesh) && attempts < maxFrames) {
                    requestAnimationFrame(check);
                    return;
                }
            }
            // Either all joints are ready (and mesh loaded) or we've exhausted our budget – show now.
            handSpace.visible = true;
        };
        requestAnimationFrame(check);
    }
    resetHandModelSkeletonToPose(handModel) {
        handModel.traverse((child) => {
            if (child instanceof SkinnedMesh) {
                child.skeleton.pose();
            }
        });
    }
    applyHandMaterial(object) {
        // Auf Mobile-VR-Geräten leichtgewichtiges Matcap-Shading nutzen.
        // Auf Desktop-Browsern das holografische Additive-Material verwenden.
        const material = capabilitiesService.isMobileVR ? this.questMaterial : this.holoMaterial;
        object.traverse((child) => {
            if (child instanceof SkinnedMesh) {
                child.material = material;
                child.renderOrder = 1;
            }
        });
    }
    createHandMatcapTexture() {
        const size = 128;
        const canvas = document.createElement("canvas");
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext("2d");
        if (ctx === null) {
            throw new Error("Could not create 2D canvas context for hand matcap texture");
        }
        const cx = size / 2;
        const base = ctx.createRadialGradient(cx, cx, 0, cx, cx, cx);
        base.addColorStop(0, "rgb(180,165,150)");
        base.addColorStop(0.75, "rgb(110,100,90)");
        base.addColorStop(1, "rgb(45,40,35)");
        ctx.fillStyle = base;
        ctx.fillRect(0, 0, size, size);
        const highlight = ctx.createRadialGradient(cx * 0.5, cx * 0.45, 0, cx * 0.5, cx * 0.45, cx * 0.75);
        highlight.addColorStop(0, "rgba(255,245,230,0.85)");
        highlight.addColorStop(1, "rgba(255,245,230,0)");
        ctx.fillStyle = highlight;
        ctx.fillRect(0, 0, size, size);
        const mask = ctx.createRadialGradient(cx, cx, cx - 1, cx, cx, cx);
        mask.addColorStop(0, "rgba(0,0,0,0)");
        mask.addColorStop(1, "rgba(0,0,0,1)");
        ctx.fillStyle = mask;
        ctx.fillRect(0, 0, size, size);
        return new CanvasTexture(canvas);
    }
}
export const webXRControllerSessionBridge = new HandTrackingControllerService();
